/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */


import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { createUser, type CreateUserRequest } from '@/api/admin/users.api'
import { getRoles } from '@/api/admin/roles.api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function UserCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateUserRequest>({
    defaultValues: {
      isActive: true,
      roleIds: [],
    },
  })

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Uživatel byl úspěšně vytvořen')
      navigate('/admin/users')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Nepodařilo se vytvořit uživatele'
      toast.error(message)
    },
  })

  const onSubmit = (data: CreateUserRequest) => {
    createMutation.mutate(data)
  }

  const selectedRoles = watch('roleIds') || []

  const toggleRole = (roleId: string) => {
    const current = selectedRoles
    if (current.includes(roleId)) {
      setValue('roleIds', current.filter((id) => id !== roleId))
    } else {
      setValue('roleIds', [...current, roleId])
    }
  }

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zpět na seznam
        </Button>
        <h1 className="text-3xl font-bold">Vytvořit uživatele</h1>
        <p className="text-muted-foreground mt-1">Vyplňte informace o novém uživateli</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email je povinný',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Neplatný formát emailu',
              },
            })}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password">Heslo *</Label>
          <Input
            id="password"
            type="password"
            {...register('password', {
              required: 'Heslo je povinné',
              minLength: {
                value: 8,
                message: 'Heslo musí mít alespoň 8 znaků',
              },
            })}
          />
          {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
        </div>

        {/* First Name */}
        <div className="space-y-2">
          <Label htmlFor="firstName">Jméno *</Label>
          <Input
            id="firstName"
            {...register('firstName', { required: 'Jméno je povinné' })}
          />
          {errors.firstName && <p className="text-sm text-destructive">{errors.firstName.message}</p>}
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <Label htmlFor="lastName">Příjmení *</Label>
          <Input
            id="lastName"
            {...register('lastName', { required: 'Příjmení je povinné' })}
          />
          {errors.lastName && <p className="text-sm text-destructive">{errors.lastName.message}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Telefon</Label>
          <Input id="phone" type="tel" {...register('phone')} />
        </div>

        {/* Avatar URL */}
        <div className="space-y-2">
          <Label htmlFor="avatar">Avatar URL</Label>
          <Input id="avatar" type="url" {...register('avatar')} />
        </div>

        {/* Is Active */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isActive"
            checked={watch('isActive')}
            onChange={(e) => setValue('isActive', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="isActive" className="cursor-pointer">
            Aktivní uživatel
          </Label>
        </div>

        {/* Roles */}
        <div className="space-y-2">
          <Label>Role *</Label>
          {rolesLoading ? (
            <p className="text-sm text-muted-foreground">Načítání rolí...</p>
          ) : (
            <div className="space-y-2 border rounded-md p-4">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`role-${role.id}`}
                    checked={selectedRoles.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor={`role-${role.id}`} className="cursor-pointer">
                    {role.name}
                    {role.description && (
                      <span className="text-sm text-muted-foreground ml-2">
                        ({role.description})
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          )}
          {selectedRoles.length === 0 && (
            <p className="text-sm text-destructive">Vyberte alespoň jednu roli</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button type="submit" disabled={createMutation.isPending || selectedRoles.length === 0}>
            {createMutation.isPending ? 'Vytváření...' : 'Vytvořit uživatele'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
            Zrušit
          </Button>
        </div>
      </form>
    </div>
  )
}
